/*
 * Nová generace Naturlabs (sections/nl-nr-*.liquid): behaviour.
 *   Hero      pointer light on the capsule tile, lazy light sweep video (only in view, never with reduced motion
 *             or Save-Data)
 *   System    accessible tabs (arrows, Home, End), auto advance while visible until the first interaction
 *   Lineup    part of day filters, rail progress and arrows, mouse drag, product drawer (<dialog>) with
 *             previous / next, Esc, backdrop click, focus return
 *   Questions height animation of <details>
 *   Notify    links to #nr-notify close the drawer, scroll and focus the e-mail field
 * Content stays fully usable without this file (filters off, drawer content not reachable is avoided by
 * keeping the cards readable). Every effect respects prefers-reduced-motion and html.nl-motion-off.
 */
(() => {
  'use strict';

  if (window.NLNovaRada) return;
  window.NLNovaRada = true;

  const root = document.documentElement;
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduced = () => mqReduced.matches || root.classList.contains('nl-motion-off');
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
  const pad = (n) => (n < 10 ? `0${n}` : String(n));

  /* ------------------------------------------------------------------
   * Hero
   * ------------------------------------------------------------------ */
  const initHero = (section) => {
    const stage = section.querySelector('[data-nr-stage]');
    if (!stage) return;
    const frame = stage.querySelector('.nl-nr-stage__frame');

    if (frame && mqFine.matches) {
      let raf = 0;
      frame.addEventListener('pointerenter', () => stage.classList.add('is-pointer'));
      frame.addEventListener('pointerleave', () => stage.classList.remove('is-pointer'));
      frame.addEventListener('pointermove', (event) => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const rect = frame.getBoundingClientRect();
          frame.style.setProperty('--nr-mx', `${((event.clientX - rect.left) / rect.width) * 100}%`);
          frame.style.setProperty('--nr-my', `${((event.clientY - rect.top) / rect.height) * 100}%`);
        });
      });
    }

    const video = stage.querySelector('[data-nr-video]');
    if (!video || reduced() || saveData() || !('IntersectionObserver' in window)) return;
    const source = video.querySelector('source[data-src]');
    let loaded = false;
    video.addEventListener('playing', () => stage.classList.add('is-playing'), { once: true });
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!loaded && source) {
              source.src = source.dataset.src;
              video.load();
              loaded = true;
            }
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
          } else if (loaded) {
            video.pause();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(stage);
  };

  /* ------------------------------------------------------------------
   * Naming system tabs
   * ------------------------------------------------------------------ */
  const initSystem = (section) => {
    const tabs = Array.from(section.querySelectorAll('[data-nr-ex-tab]'));
    const panels = Array.from(section.querySelectorAll('[data-nr-ex]'));
    if (tabs.length < 2) return;
    const card = section.querySelector('.nl-nr-system__card');
    const interval = 4200;
    let current = 0;
    let timer = 0;
    let stopped = reduced();
    let paused = false;
    let visible = false;

    const select = (index, focus) => {
      current = (index + tabs.length) % tabs.length;
      tabs.forEach((tab, i) => {
        const on = i === current;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        tab.classList.remove('is-timing');
      });
      panels.forEach((panel, i) => {
        const on = i === current;
        panel.hidden = !on;
        panel.classList.toggle('is-active', on);
      });
      if (focus) tabs[current].focus();
    };

    const schedule = () => {
      window.clearTimeout(timer);
      tabs.forEach((tab) => tab.classList.remove('is-timing'));
      if (stopped || paused || !visible) return;
      const tab = tabs[current];
      tab.style.setProperty('--nr-interval', `${interval}ms`);
      // restart the bar transition on the next frame
      requestAnimationFrame(() => requestAnimationFrame(() => tab.classList.add('is-timing')));
      timer = window.setTimeout(() => {
        select(current + 1, false);
        schedule();
      }, interval);
    };

    const stop = () => {
      stopped = true;
      window.clearTimeout(timer);
      tabs.forEach((tab) => tab.classList.remove('is-timing'));
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => {
        stop();
        select(i, false);
      });
      tab.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (event.key in keys) {
          event.preventDefault();
          stop();
          select(current + keys[event.key], true);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          stop();
          select(event.key === 'Home' ? 0 : tabs.length - 1, true);
        }
      });
    });

    if (card) {
      card.addEventListener('pointerenter', () => {
        paused = true;
        schedule();
      });
      card.addEventListener('pointerleave', () => {
        paused = false;
        schedule();
      });
      card.addEventListener('focusin', () => {
        paused = true;
        schedule();
      });
      card.addEventListener('focusout', () => {
        paused = false;
        schedule();
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          visible = entries[0].isIntersecting;
          schedule();
        },
        { threshold: 0.45 }
      ).observe(card || section);
    }
  };

  /* ------------------------------------------------------------------
   * Lineup: filters, rail, drawer
   * ------------------------------------------------------------------ */
  const initLineup = (section) => {
    const track = section.querySelector('[data-nr-track]');
    if (!track) return;
    const items = Array.from(track.querySelectorAll('[data-nr-item]'));
    const filters = Array.from(section.querySelectorAll('[data-nr-filter]'));
    const prev = section.querySelector('[data-nr-prev]');
    const next = section.querySelector('[data-nr-next]');
    const thumb = section.querySelector('[data-nr-thumb]');
    const bar = thumb ? thumb.parentElement : null;
    const currentEl = section.querySelector('[data-nr-current]');
    const ofEl = section.querySelector('[data-nr-of]');

    const visibleItems = () => items.filter((item) => !item.hidden);
    const step = () => {
      const first = visibleItems()[0];
      if (!first) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const shown = visibleItems();
      if (bar && thumb) {
        const ratio = track.scrollWidth > 0 ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;
        const barW = bar.clientWidth;
        const thumbW = Math.max(24, barW * ratio);
        const x = max > 0 ? (track.scrollLeft / max) * (barW - thumbW) : 0;
        thumb.style.setProperty('--nr-thumb-w', `${thumbW}px`);
        thumb.style.setProperty('--nr-thumb-x', `${x}px`);
      }
      const s = step();
      let index = s > 0 ? Math.round(track.scrollLeft / s) + 1 : 1;
      if (max > 0 && track.scrollLeft >= max - 2) index = shown.length;
      index = Math.min(Math.max(index, 1), Math.max(shown.length, 1));
      if (currentEl) currentEl.textContent = pad(index);
      if (ofEl) ofEl.textContent = pad(shown.length);
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };

    let raf = 0;
    track.addEventListener(
      'scroll',
      () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          update();
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', update, { passive: true });

    const scrollByCards = (dir) => {
      const s = step();
      const perView = Math.max(1, Math.floor(track.clientWidth / s));
      track.scrollBy({ left: dir * s * perView, behavior: reduced() ? 'auto' : 'smooth' });
    };
    if (prev) prev.addEventListener('click', () => scrollByCards(-1));
    if (next) next.addEventListener('click', () => scrollByCards(1));

    // Mouse drag (touch and trackpads scroll natively)
    let drag = null;
    let suppressClick = false;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: track.scrollLeft, moved: false, id: event.pointerId };
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 6) {
        drag.moved = true;
        track.classList.add('is-dragging');
        try {
          track.setPointerCapture(drag.id);
        } catch (e) {
          /* capture is optional */
        }
      }
      if (drag.moved) track.scrollLeft = drag.left - dx;
    });
    const endDrag = () => {
      if (!drag) return;
      if (drag.moved) {
        suppressClick = true;
        window.setTimeout(() => {
          suppressClick = false;
        }, 0);
      }
      track.classList.remove('is-dragging');
      drag = null;
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener(
      'click',
      (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    // Filters
    filters.forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.nrFilter;
        filters.forEach((b) => b.setAttribute('aria-pressed', b === button ? 'true' : 'false'));
        let n = 0;
        items.forEach((item) => {
          const show = value === 'all' || item.dataset.nrRitual === value;
          const wasHidden = item.hidden;
          item.hidden = !show;
          item.classList.remove('is-entering');
          if (show && !reduced()) {
            item.style.setProperty('--nr-i', String(n));
            // reflow so the entrance animation restarts
            void item.offsetWidth;
            item.classList.add('is-entering');
          }
          if (show) n += 1;
          if (show && wasHidden) item.removeAttribute('aria-hidden');
        });
        track.scrollTo({ left: 0, behavior: 'auto' });
        update();
      });
    });
    track.addEventListener('animationend', (event) => {
      if (event.target.classList && event.target.classList.contains('is-entering')) {
        event.target.classList.remove('is-entering');
      }
    });

    update();
    initDrawer(section, items, visibleItems);
  };

  const initDrawer = (section, items, visibleItems) => {
    const dialog = section.querySelector('[data-nr-drawer]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const panel = dialog.querySelector('[data-nr-panel]');
    const scroller = dialog.querySelector('[data-nr-scroll]');
    const details = Array.from(dialog.querySelectorAll('[data-nr-detail]'));
    const curEl = dialog.querySelector('[data-nr-d-current]');
    const ofEl = dialog.querySelector('[data-nr-d-of]');
    let activeId = null;
    let trigger = null;
    let closing = 0;

    const order = () => visibleItems().map((item) => item.dataset.nrItem);

    const show = (id, dir) => {
      const ids = order();
      const list = ids.includes(id) ? ids : items.map((item) => item.dataset.nrItem);
      activeId = id;
      details.forEach((detail) => {
        const on = detail.dataset.nrDetail === id;
        detail.hidden = !on;
        detail.classList.remove('is-active');
        if (on) {
          detail.style.setProperty('--nr-dir', `${dir || 0}px`);
          void detail.offsetWidth;
          detail.classList.add('is-active');
          const acc = getComputedStyle(detail).getPropertyValue('--nl-acc-glow-rgb').trim();
          if (acc && panel) panel.style.setProperty('--nr-d-acc', acc);
        }
      });
      const index = list.indexOf(id);
      if (curEl) curEl.textContent = pad(index + 1);
      if (ofEl) ofEl.textContent = pad(list.length);
      if (scroller) scroller.scrollTop = 0;
    };

    const step = (dir) => {
      const ids = order();
      const list = ids.includes(activeId) ? ids : items.map((item) => item.dataset.nrItem);
      const index = list.indexOf(activeId);
      const target = list[(index + dir + list.length) % list.length];
      show(target, dir * 18);
    };

    const open = (id, from) => {
      window.clearTimeout(closing);
      trigger = from || null;
      show(id, 0);
      if (!dialog.open) dialog.showModal();
      root.classList.add('nl-nr-locked');
      requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
      const closeBtn = dialog.querySelector('[data-nr-d-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    };

    const close = (after) => {
      if (!dialog.open) {
        if (after) after();
        return;
      }
      dialog.classList.remove('is-open');
      const finish = () => {
        window.clearTimeout(closing);
        if (dialog.open) dialog.close();
        root.classList.remove('nl-nr-locked');
        if (after) after();
        else if (trigger) trigger.focus({ preventScroll: true });
      };
      if (reduced()) finish();
      else closing = window.setTimeout(finish, 420);
    };

    section.querySelectorAll('[data-nr-open]').forEach((button) => {
      button.addEventListener('click', () => open(button.dataset.nrOpen, button));
    });
    const prevBtn = dialog.querySelector('[data-nr-d-prev]');
    const nextBtn = dialog.querySelector('[data-nr-d-next]');
    const closeBtn = dialog.querySelector('[data-nr-d-close]');
    if (prevBtn) prevBtn.addEventListener('click', () => step(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => step(1));
    if (closeBtn) closeBtn.addEventListener('click', () => close());

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.target.closest && event.target.closest('input, textarea, select')) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      }
    });

    dialog.querySelectorAll('[data-nr-to-notify]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const hash = link.getAttribute('href') || '';
        if (hash.charAt(0) !== '#') return;
        event.preventDefault();
        close(() => goToNotify(hash));
      });
    });

    document.addEventListener('shopify:block:select', (event) => {
      if (!section.contains(event.target)) return;
      const item = event.target.closest && event.target.closest('[data-nr-item]');
      if (item) item.scrollIntoView({ block: 'nearest', inline: 'start' });
    });
  };

  /* ------------------------------------------------------------------
   * Notify: scroll to the form and focus the e-mail field
   * ------------------------------------------------------------------ */
  const goToNotify = (hash) => {
    const target = document.querySelector(hash);
    if (!target) return;
    target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    const input = target.querySelector('[data-nr-notify-input]');
    if (input) window.setTimeout(() => input.focus({ preventScroll: true }), reduced() ? 0 : 650);
    if (history.replaceState) history.replaceState(null, '', hash);
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href="#nr-notify"]');
    if (!link || link.hasAttribute('data-nr-to-notify')) return;
    if (!document.querySelector('#nr-notify')) return;
    event.preventDefault();
    goToNotify('#nr-notify');
  });

  /* ------------------------------------------------------------------
   * Questions: animated <details>
   * ------------------------------------------------------------------ */
  const initDetails = (section) => {
    section.querySelectorAll('[data-nr-details]').forEach((details) => {
      const summary = details.querySelector('summary');
      const body = details.querySelector('.nl-nr-faq__a');
      if (!summary || !body || !body.animate) return;
      let anim = null;
      summary.addEventListener('click', (event) => {
        if (reduced()) return;
        event.preventDefault();
        if (anim) anim.cancel();
        if (details.open) {
          const h = body.offsetHeight;
          anim = body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], {
            duration: 260,
            easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
          });
          anim.onfinish = () => {
            details.open = false;
            anim = null;
          };
        } else {
          details.open = true;
          const h = body.offsetHeight;
          anim = body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], {
            duration: 320,
            easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
          });
          anim.onfinish = () => {
            anim = null;
          };
        }
      });
    });
  };

  /* ------------------------------------------------------------------
   * Boot (and theme editor reloads)
   * ------------------------------------------------------------------ */
  const boot = (scope) => {
    const within = scope || document;
    within.querySelectorAll('[data-nr-hero]').forEach((el) => {
      if (el.dataset.nrBound) return;
      el.dataset.nrBound = '1';
      initHero(el);
    });
    within.querySelectorAll('[data-nr-system]').forEach((el) => {
      if (el.dataset.nrBound) return;
      el.dataset.nrBound = '1';
      initSystem(el);
    });
    within.querySelectorAll('[data-nr-lineup]').forEach((el) => {
      if (el.dataset.nrBound) return;
      el.dataset.nrBound = '1';
      initLineup(el);
    });
    within.querySelectorAll('.nl-nr-faq').forEach((el) => {
      if (el.dataset.nrBound) return;
      el.dataset.nrBound = '1';
      initDetails(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
